import type { SortingState } from '@tanstack/react-table';
import { Alert, SkipNav, Spinner } from '@cmsgov/design-system';
import type { ApprovalListFilters } from '@access-cms/api-client';
import { useApprovalsQuery, useClaimApproval, useFnmsRetry } from '@access-cms/api-client';
import type { ApprovalFilter } from '@access-cms/shared-constants';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ApplicationInformationBanner } from '../../components/ApplicationBanner/ApplicationInformationBanner';
import { useAuth } from '../../components/auth/AuthContext';
import { Button, ButtonVariation } from '../../components/Button';
import { SYSTEM_ROLE, useRBAC } from '../../hooks/useRBAC';
import Modal from '../../components/Modal/Modal';
import Table, { TableControls, TableFooter } from '../../components/Table';
import TableResultSummary from '../../components/TableResultSummary';
import { useAlerts, useDebounce } from '../../hooks';
import ApprovalWorkflowHistory from './components/ApprovalWorkflowHistory';
import { useApprovalsColumns } from './hooks';
import type { ApprovalListItem, ReviewStage, ReviewStatus } from './types';
import { APPROVAL_FILTER_OPTIONS } from './utils/approvalFilters';

import { getTableMinHeight, getTableMinWidth } from '@/utils';

import './Approvals.css';
import '../contract/Contract.css';

const APPROVALS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100].map((value) => ({
  label: `${value} approvals per page`,
  value: value.toString(),
}));

type ApprovalSortField = 'applicationStatus' | 'contractNumber' | 'userId' | 'legalFirstName' | 'legalLastName';
type ApprovalApiSortField = NonNullable<ApprovalListFilters['sort']>;
type ApprovalSortOrder = 'asc' | 'desc';

const APPROVAL_SORT_FIELDS: Record<ApprovalSortField, ApprovalApiSortField> = {
  applicationStatus: 'application_status',
  contractNumber: 'contract_number',
  legalFirstName: 'applicant_first_name',
  legalLastName: 'applicant_last_name',
  userId: 'applicant_eua_id',
};

function getApprovalSortField(sort: string | null): ApprovalSortField | undefined {
  return sort && Object.hasOwn(APPROVAL_SORT_FIELDS, sort) ? (sort as ApprovalSortField) : undefined;
}

function mapApprovalsResultToListItem(
  approval: NonNullable<NonNullable<ReturnType<typeof useApprovalsQuery>['data']>['data']>[number],
): ApprovalListItem {
  return {
    applicationId: approval.application_id,
    applicationStatus: approval.application_status,
    assignedToFirstName: approval.assigned_to_first_name,
    assignedToLastName: approval.assigned_to_last_name,
    assignedToUserId: approval.assigned_to_user_id,
    contractNumber: approval.contract_number,
    id: approval.id,
    legalFirstName: approval.applicant_first_name,
    legalLastName: approval.applicant_last_name,
    pooled: approval.pooled,
    reviewStage: approval.review_stage as ReviewStage,
    status: approval.status as ReviewStatus,
    userId: approval.applicant_eua_id,
  };
}

function Approvals() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dismissAlert, errorAlert } = useAlerts();
  const searchQuery = searchParams.get('keyword') ?? '';
  const sortField = getApprovalSortField(searchParams.get('sort'));
  const sortOrder: ApprovalSortOrder = searchParams.get('order') === 'asc' ? 'asc' : 'desc';
  const sortBy: SortingState = sortField ? [{ desc: sortOrder === 'desc', id: sortField }] : [];
  const selectedFilter = APPROVAL_FILTER_OPTIONS.find((option) => option.value === searchParams.get('filter'))?.value;
  const selectedFilters: ApprovalFilter[] = selectedFilter ? [selectedFilter] : [];
  const pageParam = Number(searchParams.get('page'));
  const limitParam = Number(searchParams.get('limit'));
  const currentPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const recordsPerPage = APPROVALS_PER_PAGE_OPTIONS.some((option) => option.value === String(limitParam))
    ? limitParam
    : 10;
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedKeyword = useDebounce(searchInput, 300);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [successBannerMessage, setSuccessBannerMessage] = useState<string | null>(
    () => (location.state as { successBannerMessage?: string } | null)?.successBannerMessage ?? null,
  );
  const loadErrorAlertIdRef = useRef<string | null>(null);
  const baseTestId = 'manage-approvals-page';

  useEffect(() => {
    const nextSuccessBannerMessage =
      (location.state as { successBannerMessage?: string } | null)?.successBannerMessage ?? null;

    if (!nextSuccessBannerMessage) {
      return;
    }

    setSuccessBannerMessage(nextSuccessBannerMessage);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const openApplication = useCallback(
    (applicationId: string) => {
      navigate(`/approvals/application/${applicationId}`);
    },
    [navigate],
  );

  const { user } = useAuth();
  const { hasSystemRole, isAdmin } = useRBAC();
  const isForeignNationalApprover = hasSystemRole(SYSTEM_ROLE.FOREIGN_NATIONAL);

  // ACP-247: opening an FNMS record the reviewer has not claimed first prompts them to claim it
  // (an explicit claim, alongside the implicit claim that Send to FNMS performs). This includes
  // records claimed by another reviewer, which the prompt lets them take over.
  const [claimPrompt, setClaimPrompt] = useState<ApprovalListItem | null>(null);
  const handleViewApplication = useCallback(
    (row: ApprovalListItem) => {
      const isClaimableFnms =
        row.reviewStage === 'FNMS' &&
        (isForeignNationalApprover || isAdmin) &&
        (!row.assignedToUserId || row.assignedToUserId !== user?.id);
      if (isClaimableFnms) {
        setClaimPrompt(row);

        return;
      }
      if (row.applicationId) {
        openApplication(row.applicationId);
      }
    },
    [isAdmin, isForeignNationalApprover, openApplication, user?.id],
  );

  const claimApproval = useClaimApproval({
    approvalId: claimPrompt?.id ?? '',
    onError: () => setClaimPrompt(null),
    onSuccess: () => {
      const applicationId = claimPrompt?.applicationId;
      setClaimPrompt(null);
      if (applicationId) {
        openApplication(applicationId);
      }
    },
  });
  const claimedByName = [claimPrompt?.assignedToFirstName, claimPrompt?.assignedToLastName].filter(Boolean).join(' ');
  const handleClaimYes = useCallback(() => claimApproval.mutate({}), [claimApproval]);
  const handleClaimNo = useCallback(() => {
    const applicationId = claimPrompt?.applicationId;
    setClaimPrompt(null);
    if (applicationId) {
      openApplication(applicationId);
    }
  }, [claimPrompt, openApplication]);
  const handleClaimCancel = useCallback(() => setClaimPrompt(null), []);

  // ACP-2752: the retry is reachable from the queue so a reviewer can unblock a parked
  // submission without opening the record. The roles mirror the retry endpoint, which
  // authorizes the FN approver and Administrators.
  const canRetryFnms = isForeignNationalApprover || isAdmin;
  const [retryPrompt, setRetryPrompt] = useState<ApprovalListItem | null>(null);
  const [retrySent, setRetrySent] = useState(false);
  // The queue is declared further down, so the refresh is reached through a ref rather than
  // reordering the page around this one call.
  const refetchApprovalsRef = useRef<() => void>(() => {});
  const fnmsRetry = useFnmsRetry({
    applicationId: retryPrompt?.applicationId ?? '',
    onError: () => {
      setRetryPrompt(null);
      errorAlert({
        message: "We couldn't send this application to FNMS again. Please try again.",
        title: 'Unable to retry',
      });
    },
    onSuccess: () => {
      setRetryPrompt(null);
      setRetrySent(true);
      refetchApprovalsRef.current();
    },
  });
  const handleRetryFnms = useCallback((row: ApprovalListItem) => setRetryPrompt(row), []);
  const handleRetryCancel = useCallback(() => {
    if (fnmsRetry.isPending) return;
    setRetryPrompt(null);
  }, [fnmsRetry.isPending]);
  const handleRetryConfirm = useCallback(() => fnmsRetry.mutate({}), [fnmsRetry]);

  const columns = useApprovalsColumns({
    canRetryFnms,
    currentUserId: user?.id,
    isForeignNationalApprover,
    isRetryPending: fnmsRetry.isPending,
    onRetryFnms: handleRetryFnms,
    onViewApplication: handleViewApplication,
  });

  const updateSearchParams = useCallback(
    (updates: {
      filter?: ApprovalFilter | null;
      keyword?: string;
      limit?: number;
      order?: ApprovalSortOrder | null;
      page?: number;
      sort?: ApprovalSortField | null;
    }) => {
      setSearchParams(
        (previousParams) => {
          const nextParams = new URLSearchParams(previousParams);

          if (updates.keyword !== undefined) {
            if (updates.keyword) nextParams.set('keyword', updates.keyword);
            else nextParams.delete('keyword');
          }

          if (updates.filter !== undefined) {
            if (updates.filter) nextParams.set('filter', updates.filter);
            else nextParams.delete('filter');
          }

          if (updates.page !== undefined) {
            if (updates.page === 1) nextParams.delete('page');
            else nextParams.set('page', String(updates.page));
          }

          if (updates.limit !== undefined) {
            if (updates.limit === 10) nextParams.delete('limit');
            else nextParams.set('limit', String(updates.limit));
          }

          if (updates.sort !== undefined) {
            if (updates.sort) nextParams.set('sort', updates.sort);
            else nextParams.delete('sort');
          }

          if (updates.order !== undefined) {
            if (updates.order) nextParams.set('order', updates.order);
            else nextParams.delete('order');
          }

          return nextParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSortingChange = useCallback(
    (nextSorting: SortingState) => {
      const nextSort = nextSorting[0];

      const nextSortField = nextSort && getApprovalSortField(nextSort.id);

      if (!nextSortField) {
        updateSearchParams({ order: null, page: 1, sort: null });

        return;
      }

      updateSearchParams({
        order: nextSort.desc ? 'desc' : 'asc',
        page: 1,
        sort: nextSortField,
      });
    },
    [updateSearchParams],
  );

  const approvalsQuery = useApprovalsQuery({
    filters: {
      ...(debouncedKeyword ? { keyword: debouncedKeyword } : {}),
      ...(selectedFilter ? { filter: selectedFilter } : {}),
      ...(sortField ? { order: sortOrder, sort: APPROVAL_SORT_FIELDS[sortField] } : {}),
      limit: recordsPerPage,
      page: currentPage,
    },
  });

  useEffect(() => {
    if (approvalsQuery.isError && !loadErrorAlertIdRef.current) {
      loadErrorAlertIdRef.current = errorAlert({
        message: "Sorry, we couldn't load your approvals. Please try again.",
        title: 'Unable to load approvals',
      });
    }

    return () => {
      if (loadErrorAlertIdRef.current) {
        dismissAlert(loadErrorAlertIdRef.current);
        loadErrorAlertIdRef.current = null;
      }
    };
  }, [approvalsQuery.isError, dismissAlert, errorAlert]);

  // Assigned in an effect rather than during render: the queue is declared below this
  // page's retry handlers, and writing a ref while rendering is not allowed.
  useEffect(() => {
    refetchApprovalsRef.current = () => void approvalsQuery.refetch();
  }, [approvalsQuery]);

  const paginationMeta = approvalsQuery.data?.meta?.pagination;
  const totalRecords = paginationMeta?.total ?? 0;
  const totalPages = paginationMeta?.totalPages ?? 0;

  const approvalRows = useMemo(
    () => (approvalsQuery.data?.data ?? []).map(mapApprovalsResultToListItem),
    [approvalsQuery.data?.data],
  );

  const visibleData = approvalRows;

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      updateSearchParams({ keyword: value, page: 1 });
    },
    [updateSearchParams],
  );

  const handleFiltersApply = useCallback(
    (filters: string[]) => {
      const nextFilter = APPROVAL_FILTER_OPTIONS.find((option) => option.value === filters[0])?.value ?? null;
      updateSearchParams({ filter: nextFilter, page: 1 });
    },
    [updateSearchParams],
  );

  const hasActiveFilter = selectedFilter !== undefined;
  const isSearchSettled = searchInput === debouncedKeyword;
  const isResultSettled = isSearchSettled && !approvalsQuery.isFetching && !approvalsQuery.isError;
  const resultCount = totalRecords;
  const getRowId = useCallback((row: ApprovalListItem) => row.id ?? '', []);

  const handleExpandAll = useCallback(() => {
    const allExpanded = visibleData.every((row) => expanded[row.id ?? '']);
    if (allExpanded) {
      setExpanded({});
    } else {
      setExpanded(
        visibleData.reduce<Record<string, boolean>>((acc, row) => {
          if (row.id) acc[row.id] = true;

          return acc;
        }, {}),
      );
    }
  }, [visibleData, expanded]);

  const expandAllLabel = useMemo(() => {
    const allExpanded = visibleData.length > 0 && visibleData.every((row) => expanded[row.id ?? '']);

    return allExpanded ? 'Collapse All' : 'Expand All';
  }, [visibleData, expanded]);

  const isEmpty = visibleData.length === 0;
  const tableMinHeight = getTableMinHeight(recordsPerPage);
  const tableMinWidth = getTableMinWidth(columns);

  return (
    <>
      <SkipNav href='#content'>Skip to page content</SkipNav>
      <div
        className='ds-l-row'
        data-testid={baseTestId}>
        <article
          aria-labelledby='page-heading'
          className='ds-content ds-l-col--12'
          data-testid={`${baseTestId}-content`}
          id='content'>
          {successBannerMessage && (
            <Alert
              className='ds-u-margin-bottom--3'
              data-testid={`${baseTestId}-success-banner`}
              role='status'
              variation='success'>
              <p
                className='ds-u-margin-bottom--0 manage-approvals__success-banner-message'
                data-testid={`${baseTestId}-success-banner-message`}>
                {successBannerMessage}
              </p>
            </Alert>
          )}
          {/* ACP-2752: a retry fired from this table never opens the record, so the
              confirmation has to live here. It says what was sent and where the outcome
              will show, without claiming FNMS has accepted it yet. */}
          {retrySent && (
            <ApplicationInformationBanner
              bannerId='manage-approvals-fnms-retry'
              data-testid={`${baseTestId}-retry-banner`}
              dismissalScope='mount'
              heading='The application was sent to FNMS'
              onDismiss={() => setRetrySent(false)}>
              <ul
                className='ds-u-margin-bottom--0'
                data-testid={`${baseTestId}-retry-banner-body`}>
                <li>
                  To see whether the application has been saved in FNMS, open it. The &ldquo;Approve&rdquo; and
                  &ldquo;Decline and Delete&rdquo; buttons will be enabled.
                </li>
                <li>
                  If the application is not saved in FNMS, the table will show the &ldquo;FNMS Save Failed&rdquo;
                  status, and the Retry button will be enabled.
                </li>
              </ul>
            </ApplicationInformationBanner>
          )}
          <h1
            data-testid={`${baseTestId}-heading`}
            id='page-heading'>
            Manage Approvals
          </h1>
          <p
            className='manage-approvals__intro'
            data-testid={`${baseTestId}-intro`}>
            To view an application, click the View Application link in the Action column.
          </p>
          <div
            className='contract-roster-tab-content'
            data-testid={`${baseTestId}-body`}>
            <div className='manage-approvals__modern-container'>
              <div className='access-table-shell manage-approvals__table-container'>
                <TableControls
                  singleSelect
                  className='manage-approvals__table-toolbar'
                  data-testid={`${baseTestId}-controls`}
                  expandCollapseLabel={expandAllLabel}
                  filterOptions={APPROVAL_FILTER_OPTIONS}
                  resultCount={isResultSettled ? resultCount : undefined}
                  searchInput={searchInput}
                  searchPlaceholder='Search...'
                  selectedFilters={selectedFilters}
                  showExpandCollapse={visibleData.length > 0}
                  onExpandCollapse={handleExpandAll}
                  onFiltersApply={handleFiltersApply}
                  onSearchChange={handleSearchChange}
                />

                <div
                  className='access-table-wrapper manage-approvals__table-wrapper'
                  data-testid={`${baseTestId}-table`}>
                  <div style={{ minHeight: `${tableMinHeight}px`, minWidth: `${tableMinWidth}px` }}>
                    {approvalsQuery.isLoading ? (
                      <div
                        className='access-table-status'
                        data-testid={`${baseTestId}-loading`}>
                        <div className='access-table-status__card'>
                          <Spinner aria-valuetext='Loading approvals...' />
                        </div>
                      </div>
                    ) : isEmpty ? (
                      <div
                        className='access-table-status'
                        data-testid={`${baseTestId}-empty`}>
                        <div className='access-table-status__card'>
                          <p
                            className='access-table-status__message'
                            data-testid={`${baseTestId}-empty-message`}>
                            {approvalsQuery.isError
                              ? 'Approvals couldn\u2019t be loaded.'
                              : debouncedKeyword || hasActiveFilter
                              ? 'No approvals match your search or filter criteria.'
                              : 'No approvals available.'}
                          </p>
                          {!approvalsQuery.isError && (debouncedKeyword || hasActiveFilter) && (
                            <p
                              className='access-table-status__hint'
                              data-testid={`${baseTestId}-empty-hint`}>
                              Try adjusting your search terms or filters.
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Table
                        enableSorting
                        manualSorting
                        ariaLabel='Manage approvals table'
                        caption={`Manage approvals table showing ${totalRecords} entries`}
                        className='manage-approvals__table'
                        columns={columns}
                        data={visibleData}
                        data-testid={`${baseTestId}-table-component`}
                        expanded={expanded}
                        getRowId={getRowId}
                        renderExpandedRow={(row) => (
                          <ApprovalWorkflowHistory
                            data-testid={`${baseTestId}-workflow-history-${row.id}`}
                            row={row}
                          />
                        )}
                        sortBy={sortBy}
                        onExpandedChange={setExpanded}
                        onSortingChange={handleSortingChange}
                      />
                    )}
                  </div>
                </div>

                {!approvalsQuery.isLoading && !isEmpty && (
                  <TableFooter
                    className='manage-approvals__table-footer'
                    currentPage={currentPage}
                    data-testid={`${baseTestId}-footer`}
                    recordsDropdownName='manage-approvals-records-per-page'
                    recordsPerPage={recordsPerPage}
                    recordsPerPageOptions={APPROVALS_PER_PAGE_OPTIONS}
                    totalPages={totalPages}
                    onPageChange={(page) => updateSearchParams({ page })}
                    onRecordsPerPageChange={(limit) => updateSearchParams({ limit, page: 1 })}
                  />
                )}
              </div>
              {!approvalsQuery.isLoading && !isEmpty && (
                <TableResultSummary
                  data-testid={`${baseTestId}-summary`}
                  pagination={paginationMeta}
                  rowCount={visibleData.length}
                />
              )}
            </div>
          </div>
        </article>
      </div>

      {/* ACP-247: explicit Claim prompt for an Available FNMS pool record. */}
      <Modal
        className='manage-approvals__claim-modal'
        data-testid='manage-approvals-claim-modal'
        heading={
          claimPrompt
            ? `Do you want to claim Application - ${claimPrompt.legalLastName}, ${claimPrompt.legalFirstName}?`
            : ''
        }
        isOpen={claimPrompt !== null}
        onClose={handleClaimCancel}>
        <p
          className='manage-approvals__claim-modal-body'
          data-testid='manage-approvals-claim-modal-body'>
          {claimPrompt?.assignedToUserId
            ? `This application is currently claimed by ${claimedByName}. Would you like to claim it under your name? `
            : 'This application is not under review yet. Would you like to claim this application to review it? '}
          Select &ldquo;No&rdquo; to view the application without claiming it. You can open this application again to
          claim it later.
        </p>
        <div
          className='modal-actions manage-approvals__claim-modal-actions'
          data-testid='manage-approvals-claim-modal-actions'>
          <Button
            bordered
            isAutoWidth
            data-testid='manage-approvals-claim-modal-cancel'
            disabled={claimApproval.isPending}
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleClaimCancel}>
            Cancel
          </Button>
          <Button
            bordered
            isAutoWidth
            data-testid='manage-approvals-claim-modal-no'
            disabled={claimApproval.isPending}
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleClaimNo}>
            No
          </Button>
          <Button
            isAutoWidth
            data-testid='manage-approvals-claim-modal-yes'
            disabled={claimApproval.isPending}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleClaimYes}>
            Yes
          </Button>
        </div>
      </Modal>
      {/* The same confirmation the record asks for. An icon in a list is far easier to
          hit by accident than a button inside a record the reviewer opened on purpose. */}
      <Modal
        className='manage-approvals__retry-modal'
        data-testid='manage-approvals-retry-modal'
        heading='Retry the FNMS submission?'
        isOpen={retryPrompt !== null}
        onClose={handleRetryCancel}>
        <p data-testid='manage-approvals-retry-modal-body'>
          The automatic attempts to reach FNMS were used up, so this application is waiting here instead of moving on.
          This sends it to FNMS again.
        </p>
        <div
          className='modal-actions'
          data-testid='manage-approvals-retry-modal-actions'>
          <Button
            bordered
            isAutoWidth
            data-testid='manage-approvals-retry-modal-cancel'
            disabled={fnmsRetry.isPending}
            type='button'
            variation={ButtonVariation.GHOST}
            onClick={handleRetryCancel}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            data-testid='manage-approvals-retry-modal-confirm'
            disabled={fnmsRetry.isPending}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={handleRetryConfirm}>
            Retry Send to FNMS
          </Button>
        </div>
      </Modal>
    </>
  );
}

export default Approvals;
